<template>
  <v-container>
    <v-row justify="space-between" align="center" class="mb-4">
      <h2 class="text-h5">User Management</h2>
      <v-btn color="primary" @click="isAddDialogOpen = true">
        + Add User
      </v-btn>
    </v-row>

    <!-- Add User Dialog -->
    <v-dialog v-model="isAddDialogOpen" max-width="600px">
      <v-card>
        <!-- Top Section: Title and Cancel Button -->
        <v-card-title class="d-flex justify-space-between align-center">
          <span class="text-h6">Add New User</span>
          <v-btn icon @click="isAddDialogOpen = false">
            <v-icon color="red">mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <!-- Form Section -->
        <v-card-text>
          <v-form ref="addUserForm">
            <v-container>
              <v-row>
                <v-col cols="12" md="6">
                  <!-- Name Field -->
                  <v-text-field v-model="newUser.first_name" label="First Name" outlined required></v-text-field>
                </v-col>
                <v-col cols="12" md="6"><v-text-field v-model="newUser.last_name" label="Last Name" outlined
                    required></v-text-field></v-col>
              </v-row>

              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field v-model="newUser.email" label="Email" outlined required></v-text-field></v-col>
                <v-col cols="12" md="6">
                  <!-- Phone Field -->
                  <v-text-field v-model="newUser.username" label="Phone" type="tel" outlined
                    required></v-text-field></v-col>
              </v-row>

              <v-row>
                <v-col cols="12" md="6">
                  <!-- Password Field -->
                  <v-text-field v-model="newUser._password" label="Password" type="password" outlined
                    required></v-text-field></v-col>
                <v-col cols="12" md="6">
                  <!-- Assigned Class Dropdown -->
                  <v-select v-model="newUser.class_info" :items="filteredClasses" item-value="id" item-text="name"
                    item-title="name" label="Assigned Class" outlined required multiple></v-select></v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>

        <!-- Bottom Section: Save Button -->
        <v-card-actions class="d-flex justify-end">
          <v-btn class="save-button" dark elevation="2" rounded @click="addUser">
            Save
          </v-btn>
        </v-card-actions>
      
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import axios from "axios";
import { apiConfig } from "@/config/apiConfig";
import { defineProps, ref, reactive,watch, computed, onMounted } from "vue";
import { getUser, postUser, } from "@/services/apiServices";


// Define props
const props = defineProps({
  classsInfoList: {
    type: Array,
    required: true,
  },
  users: {
    type: Array,
    required: true,
  },
});






const isAddDialogOpen = ref(false);

const mapUserResponseToModel = (response) => {
  // Assuming the response has user data and class_info as separate objects
  if (response && response.length > 0) {
    // Iterate through each user in the response array
    response.forEach((item) => {
      if (item && item.user && item.class_info) {
        const { user, class_info } = item;

        // Create a new model for each user
        const newUser = {
          user_id: user.id || '',
          first_name: user.first_name || '',
          last_name: user.last_name || '',
          username: user.username || '',
          email: user.email || '',
          _password: user._password || '',
          //class_info: class_info ? class_info.map((cls) => cls.id) : [],
          class_info: Array.isArray(class_info)
            ? class_info.map((cls) => ({
              id: cls.id || '',
              name: cls.name || ''
            }))
            : [],
        };
      } else {
        console.error("Invalid user data in response:", item);
      }
    });
  } else {
    console.error("Error: User object is not available in the response");
  }
};

const error = ref("");
const emit = defineEmits(["addedUser"]);
const newUser = reactive({
  user_id: "",
  first_name: "",
  last_name: "",
  username: "",
  email: "",
  _password: "",
  class_info: [],
});


const filteredClasses = computed(() => {
  return props.classsInfoList.filter(classItem =>
    !props.users.some(user =>
      user.class_info.some(classInfo => classInfo.id === classItem.id)
    )
  );
})

watch(isAddDialogOpen, (newVal) => {
  if (!newVal) {
    resetForm();  
  }
});

// Reset form fields without affecting filtered classes
const resetForm = () => {
  newUser.user_id = "";
  newUser.first_name = "";
  newUser.last_name = "";
  newUser.username = "";
  newUser.email = "";
  newUser._password = "";
  newUser.class_info = [];  // Reset only the class selection
};


const fetchUser = async () => {
  error.value = "";
  try {
    const res = await getUser();
    mapUserResponseToModel(res);
  } catch (err) {
    error.value = err.response?.data?.message || "Failed to load users.";
    console.error("Error fetching users:", err);
  }
};

const addUser = async () => {
  if (
    newUser.first_name &&
    newUser.username &&
    newUser._password &&
    newUser.class_info.length > 0
  ) {
    try {
      newUser.class_info = newUser.class_info.map(Number);
      const res = await postUser(
        newUser.first_name,
        newUser.last_name,
        newUser.username,
        newUser.email,
        newUser._password,
        newUser.class_info
      );
      emit("addedUser",res)
      isAddDialogOpen.value = false;
    } catch (err) {
      error.value = err.response?.data?.message || "Failed to add user.";
      console.error("Error adding user:", err);
    }
  } else {
    alert("Please fill out all fields.");
  }
};

onMounted(fetchUser);
</script>
